/**
 * Composable для сбора статистики WebRTC соединения
 *
 * Отвечает за:
 * - Сбор статистики с RTCPeerConnection
 * - Формирование метрик для отправки собеседнику
 */

import { ref, type Ref } from 'vue';
import {
  AudioDeviceState,
  CameraState,
  ConnectionStats,
  ConnectionType,
  ConnectionTypeEnum,
  PeerMetrics,
  QualityLevel
} from '../types';
import { collectStatsFromReport, determineConnectionType, statsCache } from '../utils/webrtcStats';

// Узкий тип для codec-репорта: он есть в отчётах, но не в стандартных DOM-типах TS
const isCodec = (r: RTCStats | undefined): r is RTCCodecStatsLike =>
    !!r && r.type === 'codec';

export function useConnectionStats() {
  // ==================== СОСТОЯНИЕ ====================

  /** Агрегированные сетевые метрики */
  const stats: Ref<ConnectionStats> = ref({
    rtt: 0,
    bitrateOut: 0,
    bitrateIn: 0,
    packetLoss: 0
  });

  /** Кодеки */
  const codecs: Ref<{ video: string | null; audio: string | null }> = ref({
    video: null,
    audio: null
  });

  /** Тип соединения */
  const connectionType: Ref<ConnectionType> = ref(ConnectionTypeEnum.Unknown);

  // ==================== СБОР СТАТИСТИКИ ====================

  /**
   * Собирает статистику с RTCPeerConnection
   */
  async function collectStats(peerConnection: RTCPeerConnection | null): Promise<void> {
    if (!peerConnection) return;

    try {
      // Используем утилиту для сбора базовой статистики
      const collected = await collectStatsFromReport(peerConnection, statsCache);
      
      // Дополнительно собираем кодеки
      const rtcStats = await peerConnection.getStats();
      rtcStats.forEach((report: RTCStats) => {
        if (report.type === 'outbound-rtp' && (report as any).codecId) {
          const codecReport = rtcStats.get((report as any).codecId);
          if (isCodec(codecReport) && codecReport.mimeType) {
            const kind = (report as any).kind;
            if (kind === 'video') codecs.value.video = codecReport.mimeType;
            else if (kind === 'audio') codecs.value.audio = codecReport.mimeType;
          }
        }
      });

      // Определяем тип соединения
      const currentConnectionType = determineConnectionType(
        collected.localCandidate?.candidateType,
        collected.remoteCandidate?.candidateType
      );

      // Обновляем состояние
      stats.value = {
        rtt: collected.rtt,
        bitrateOut: collected.bitrateOut,
        bitrateIn: collected.bitrateIn,
        packetLoss: collected.packetLoss || stats.value.packetLoss
      };
      connectionType.value = currentConnectionType as ConnectionType;
    } catch (error) {
      console.error('❌ Ошибка сбора статистики:', error);
    }
  }

  // ==================== ЭКСПОРТ МЕТРИК ====================

  /**
   * Формирует метрики для отправки собеседнику
   */
  function getPeerMetrics(
      currentQuality: QualityLevel,
      cameraInfo: CameraState,
      audioDeviceInfo: AudioDeviceState, // оставлен для совместимости, сейчас не используется
      localStream: MediaStream | null
  ): PeerMetrics {
    const videoTrack = localStream?.getVideoTracks()[0];
    const audioTrack = localStream?.getAudioTracks()[0];
    const videoSettings = videoTrack?.getSettings();

    return {
      codecs: codecs.value,
      resolution: {
        width: videoSettings?.width || 0,
        height: videoSettings?.height || 0
      },
      network: {
        rtt: stats.value.rtt,
        bitrateOut: stats.value.bitrateOut,
        bitrateIn: stats.value.bitrateIn,
        packetLoss: stats.value.packetLoss,
        connectionType: connectionType.value
      },
      media: {
        audioEnabled: !!audioTrack?.enabled,
        videoEnabled: !!videoTrack?.enabled
      },
      camera: {
        type: cameraInfo.currentCamera?.type || 'unknown',
        label: cameraInfo.currentCamera?.label || 'Unknown Camera',
        canSwitch: cameraInfo.canSwitchCameras,
        availableCameras: cameraInfo.availableCameras.length
      },
      quality: currentQuality
    };
  }

  // ==================== ВОЗВРАЩАЕМЫЙ API ====================

  return {
    // Состояние
    stats,
    connectionType,

    // Сбор/сброс статистики
    collectStats,

    // Экспорт метрик
    getPeerMetrics
  };
}
